import { useEffect, useState } from "react";
import type { Band } from "../shared/economy";
import { sounds } from "./effects";

// The moment. A night-sky cutscene: the route draws itself up the mountain,
// the flag plants at the peak, GOAL MET appears with snow and confetti. Plays
// once when today's target is crossed, then hands the calm light UI back.
export function LaunchOverlay({ band, onDone, silent=false }: { band: Band; onDone: () => void; silent?:boolean }) {
  const [phase, setPhase] = useState<"climb" | "planted" | "closing">("climb");

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if(!silent)sounds.liftoff(band);
    const timers = reduced
      ? [window.setTimeout(() => setPhase("planted"), 150), window.setTimeout(() => setPhase("closing"), 2100), window.setTimeout(onDone, 2600)]
      : [
          window.setTimeout(() => setPhase("planted"), 1500),
          window.setTimeout(() => setPhase("closing"), 3500),
          window.setTimeout(onDone, 4000),
        ];
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [band, onDone, silent]);

  return (
    <div className={`launch-overlay phase-${phase}`} onClick={onDone} role="presentation">
      <div className="launch-word">GOAL MET</div>
      <div className="launch-sub">Required work done · free time unlocked 🎉</div>
      <MountainArt />
      {Array.from({ length: 18 }, (_, index) => (
        <i
          key={`s${index}`}
          className="snowflake"
          style={{
            left: `${(index * 41) % 100}vw`,
            animationDelay: `${(index % 9) * 0.35}s`,
            animationDuration: `${5 + (index % 4)}s`,
          }}
        />
      ))}
      {Array.from({ length: 24 }, (_, index) => (
        <i
          key={`p${index}`}
          className="summit-particle"
          style={{
            background: ["#ffd54d", "#ff8a3d", "#8ee0b4", "#4dd8ff", "#f472b6"][index % 5],
            animationDelay: `${1.5 + (index % 8) * 0.05}s`,
            ["--dx" as string]: `${((index * 53) % 320) - 160}px`,
            ["--dy" as string]: `${-40 - ((index * 31) % 220)}px`,
          }}
        />
      ))}
    </div>
  );
}

function MountainArt() {
  return (
    <svg className="summit-mountain" viewBox="0 0 400 300" aria-hidden="true">
      <defs>
        <linearGradient id="peak-snow" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#f4f8ff" />
          <stop offset="1" stopColor="#b9c9e2" />
        </linearGradient>
        <linearGradient id="peak-rock" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#3d5178" />
          <stop offset="1" stopColor="#1d2a47" />
        </linearGradient>
      </defs>
      {/* back ridges */}
      <path d="M-10 300 L80 190 L150 260 L220 170 L300 250 L410 160 L410 300 Z" fill="#141e38" />
      {/* main peak */}
      <path d="M60 300 L200 60 L340 300 Z" fill="url(#peak-rock)" />
      {/* snow cap */}
      <path d="M200 60 L246 138 L228 128 L214 144 L196 126 L178 142 L162 130 Z" fill="url(#peak-snow)" />
      {/* the route, drawn upward during the climb phase */}
      <path
        className="summit-route"
        d="M110 298 C150 260 140 240 176 214 C210 190 180 170 200 140 C212 120 196 100 200 74"
        fill="none" stroke="#ffd54d" strokeWidth="4" strokeLinecap="round" strokeDasharray="6 10"
      />
      {/* flag, planted at the peak */}
      <g className="summit-flag">
        <line x1="200" y1="72" x2="200" y2="34" stroke="#e8ecf7" strokeWidth="4" strokeLinecap="round" />
        <path className="summit-flag-cloth" d="M202 36 L238 46 L202 58 Z" fill="#ff5a3d" />
      </g>
    </svg>
  );
}
